import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { fetchNotifications } from '@/api/notifications';
import { useAuth } from '@/contexts/AuthContext';
import { debug } from '@/utils/logger';

interface NotificationsBadgeContextValue {
  unreadCount: number;
  setUnreadCount: (count: number) => void;
  refreshUnreadCount: () => Promise<void>;
}

const NotificationsBadgeContext = createContext<NotificationsBadgeContextValue | undefined>(undefined);

export const NotificationsBadgeProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnreadCount = useCallback(async () => {
    if (!user?._id) {
      setUnreadCount(0);
      return;
    }

    try {
      const list = await fetchNotifications(user._id);
      const count = list.filter((notification) => notification.status !== 'read').length;
      setUnreadCount(count);
    } catch (err) {
      debug('[NotificationsBadgeContext] Notifications count fetch failed', err);
    }
  }, [user?._id]);

  useEffect(() => {
    void refreshUnreadCount();
  }, [refreshUnreadCount]);

  const value = useMemo(
    () => ({
      unreadCount,
      setUnreadCount,
      refreshUnreadCount,
    }),
    [unreadCount, refreshUnreadCount]
  );

  return (
    <NotificationsBadgeContext.Provider value={value}>
      {children}
    </NotificationsBadgeContext.Provider>
  );
};

export const useNotificationsBadge = (): NotificationsBadgeContextValue => {
  const context = useContext(NotificationsBadgeContext);
  if (!context) {
    throw new Error('useNotificationsBadge must be used within a NotificationsBadgeProvider');
  }
  return context;
};

